// FILE: HomeView.tsx
// Purpose: The screen behind `/` — where a session starts before any thread is
//          open.
// Layer: Web components
// Depends on: usePrimaryNewThread (same action as the sidebar button), authStore
//
// Deliberately one decision wide: brand, who you are, and the single thing
// worth doing from here. Recent threads and project pickers live in the
// sidebar, which is on screen right next to this; repeating them here would
// just be a second, staler copy.

import { useQuery } from "@tanstack/react-query";
import type { CSSProperties } from "react";

import { useAuthStore } from "../auth/authStore";
import { usePrimaryNewThread } from "../hooks/usePrimaryNewThread";
import { shortcutLabelForCommand } from "../keybindings";
import { homeGreetingTitle } from "../lib/homeGreeting";
import { serverConfigQueryOptions } from "../lib/serverReactQuery";
import { SquarePenIcon } from "../lib/icons";
import { Button } from "./ui/button";
import { ShortcutKbd } from "./ui/shortcut-kbd";
import { SidebarInset } from "./ui/sidebar";

const HOME_SUBTITLE = "Abre un chat nuevo y pon a trabajar a tus agentes.";
const NO_PROJECT_HINT = "Aún no tienes proyectos asignados. Pide acceso a un administrador.";

/** Entrance order on screen: mark, greeting, action. */
const revealDelay = (step: 1 | 2 | 3): CSSProperties =>
  ({ "--home-reveal-delay": `${(step - 1) * 70}ms` }) as CSSProperties;

export function HomeView() {
  // The root route holds the app back until auth settles, so by the time this
  // renders the user is known. The optional chain covers the empty-name case,
  // which `homeGreetingTitle` turns into a greeting without a name.
  const userName = useAuthStore((state) => state.user?.name ?? null);
  const { canStartNewThread, startNewThread } = usePrimaryNewThread();
  const serverConfigQuery = useQuery(serverConfigQueryOptions());
  const keybindings = serverConfigQuery.data?.keybindings ?? [];
  const newThreadShortcutLabel =
    shortcutLabelForCommand(keybindings, "chat.newLocal") ??
    shortcutLabelForCommand(keybindings, "chat.new");

  return (
    <SidebarInset className="h-dvh min-h-0 overflow-hidden overscroll-y-none bg-background text-foreground">
      <div className="relative flex min-h-0 flex-1 items-center justify-center px-6">
        {/* Light behind the mark, so the centre of the screen is not a flat
            field. Same radial vocabulary as the sidebar gap and error screen. */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 bg-[radial-gradient(32rem_20rem_at_50%_38%,color-mix(in_srgb,var(--foreground)_5%,transparent),transparent_72%)]"
        />

        <div className="relative flex w-full max-w-md flex-col items-center text-center">
          <img
            src="/taskin-logo.png"
            alt="Taskin"
            // Intrinsic size of the shipped PNG; declaring it keeps the block
            // from reflowing once the image decodes.
            width={1098}
            height={271}
            draggable={false}
            className="home-reveal h-auto w-44 select-none opacity-95 sm:w-52"
            style={revealDelay(1)}
          />

          <div className="home-reveal mt-8" style={revealDelay(2)}>
            <h1 className="text-xl font-semibold tracking-tight text-balance sm:text-2xl">
              {homeGreetingTitle(userName)}
            </h1>
            <p className="mt-2 text-sm leading-relaxed text-balance text-muted-foreground">
              {canStartNewThread ? HOME_SUBTITLE : NO_PROJECT_HINT}
            </p>
          </div>

          <div className="home-reveal mt-7 flex flex-col items-center gap-3" style={revealDelay(3)}>
            <Button
              size="xl"
              className="px-5"
              disabled={!canStartNewThread}
              onClick={() => {
                startNewThread();
              }}
            >
              <SquarePenIcon />
              Nuevo chat
            </Button>
            {canStartNewThread && newThreadShortcutLabel ? (
              <ShortcutKbd
                shortcutLabel={newThreadShortcutLabel}
                groupClassName="gap-1"
                className="h-5 min-w-5 px-1.5 text-[length:var(--app-font-size-ui-2xs,9px)] text-muted-foreground/72"
              />
            ) : null}
          </div>
        </div>
      </div>
    </SidebarInset>
  );
}

export default HomeView;
